import {Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {EmojiModule} from '@ctrl/ngx-emoji-mart/ngx-emoji';
import {PickerComponent} from '@ctrl/ngx-emoji-mart';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {ClickOutsideDirective} from '../../directives/click-outside.directive';
import {EmojiCategoryNavigationDirective} from '../../directives/emoji-category-navigation.directive';

// Type definitions
type EmojiSet = '' | 'apple' | 'google' | 'twitter' | 'facebook';
type EmojiSkin = 1 | 2 | 3 | 4 | 5 | 6;

@Component({
  selector: 'app-panel-emojies',
  standalone: true,
  imports: [
    CommonModule,
    EmojiModule,
    PickerComponent,
    ClickOutsideDirective,
    EmojiCategoryNavigationDirective
  ],
  templateUrl: './panel-emojies.component.html',
  styleUrls: ['./panel-emojies.component.scss'],
  animations: [
    trigger('pickerAnimation', [
      state('void', style({
        transform: 'translateY(10px)',
        opacity: 0
      })),
      state('visible', style({
        transform: 'translateY(0)',
        opacity: 1
      })),
      transition('void => visible', [
        animate('{{ enterTiming }}ms {{ enterEasing }}') // Use Axede timings/easings
      ], { params: { enterTiming: 150, enterEasing: 'cubic-bezier(0.0, 0.0, 0.2, 1)' } }), // Default: ease-decelerate
      transition('visible => void', [
        animate('{{ leaveTiming }}ms {{ leaveEasing }}') // Use Axede timings/easings
      ], { params: { leaveTiming: 100, leaveEasing: 'cubic-bezier(0.4, 0.0, 1, 1)' } }) // Default: ease-accelerate
    ])
  ]
})
export class PanelEmojiesComponent {
  @ViewChild('emojiPickerContainer') emojiPickerContainerRef!: ElementRef;

  @Input() maxEmojiPickerHeight = 350;
  @Input() isDarkMode = false;
  @Input() buttonLabel = 'Abrir selector de emojis';
  @Input() emojiPickerTitle = 'Selecciona un emoji...';
  @Input() disabled = false;

  @Output() emojiSelected = new EventEmitter<string>();

  showEmojiPicker = false;

  // Emoji configuration
  emojiSize = 24;
  perLine = 8;
  set: EmojiSet = 'apple';
  selectedSkin: EmojiSkin = 1;

  // Spanish i18n configuration for emoji-mart
  emojiI18n = {
    search: 'Buscar',
    clear: 'Limpiar',
    notfound: 'No se encontraron emojis',
    skintext: 'Elige un tono de piel',
    categories: {
      search: 'Resultados de búsqueda',
      recent: 'Usados frecuentemente',
      smileys: 'Emoticonos',
      people: 'Personas',
      nature: 'Animales y Naturaleza',
      foods: 'Comida y Bebida',
      activity: 'Actividades',
      places: 'Viajes y Lugares',
      objects: 'Objetos',
      symbols: 'Símbolos',
      flags: 'Banderas',
      custom: 'Personalizado',
    },
    categorieslabel: 'Categorías de emojis',
    skintones: {
      1: 'Tono de piel predeterminado',
      2: 'Tono de piel claro',
      3: 'Tono de piel medio-claro',
      4: 'Tono de piel medio',
      5: 'Tono de piel medio-oscuro',
      6: 'Tono de piel oscuro',
    },
  };

  // Search icons configuration
  searchIcons = {
    search: 'M12.9 14.32a8 8 0 1 1 1.41-1.41l5.35 5.33-1.42 1.42-5.33-5.34zM8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12z',
    clear: 'M10 8.586L2.929 1.515 1.515 2.929 8.586 10l-7.071 7.071 1.414 1.414L10 11.414l7.071 7.071 1.414-1.414L11.414 10l7.071-7.071-1.414-1.414L10 8.586z'
  };

  // Add additional class to help with styling
  emojiMartClass = 'custom-emoji-mart-nav-bottom';

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    // Adjust height to account for navigation at bottom if needed
    const navigationHeight = 40; // Approximate height of navigation bar
    if (this.maxEmojiPickerHeight > navigationHeight) {
      // Ensure there's room for the navigation bar
      this.maxEmojiPickerHeight -= 0; // No need to adjust as we're using flex
    }
  }

  toggleEmojiPicker(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showEmojiPicker = !this.showEmojiPicker;

    if (this.showEmojiPicker) {
      setTimeout(() => this.adjustPickerPosition(), 0);
    }
  }

  adjustPickerPosition(): void {
    const pickerElement = this.emojiPickerContainerRef?.nativeElement;
    if (pickerElement) {
      const rect = pickerElement.getBoundingClientRect();
      if (rect.top < 0) {
        pickerElement.style.bottom = 'auto';
        pickerElement.style.top = '100%';
        pickerElement.style.marginTop = '8px';
        pickerElement.style.marginBottom = '0';
      }
    }
  }

  onSkinChange(skin: EmojiSkin): void {
    this.selectedSkin = skin;
  }

  addEmoji(event: any): void {
    if (!event || !event.emoji) return;

    const emoji = event.emoji.native;
    this.emojiSelected.emit(emoji);
  }

  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    const emojiButton = this.elementRef.nativeElement.querySelector('.emoji-toggle-btn');

    if (!emojiButton.contains(target)) {
      this.showEmojiPicker = false;
    }
  }
}
